import { colors } from '@/theme'
import { Autocomplete, Box, MenuItem, Select, TextField, Typography } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { enGB } from 'date-fns/locale/en-GB'
import { parse, format, isValid } from 'date-fns'
import type { ReactNode } from 'react'
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form'

interface FormFieldProps<T extends FieldValues> {
  name: Path<T>
  label: string
  control: Control<T>
  isEditMode: boolean
  disabled?: boolean
  type?: 'text' | 'select' | 'autocomplete' | 'date'
  /** Suggestions for `select` (which restricts to them) and `autocomplete` (which does not). */
  options?: string[]
  maxLength?: number
  children?: ReactNode // For icon
  error?: string
  formatDisplayValue?: (value: string) => string
}

export default function FormField<T extends FieldValues>({
  name,
  label,
  control,
  isEditMode,
  disabled = false,
  type = 'text',
  options,
  maxLength,
  children,
  error,
  formatDisplayValue
}: FormFieldProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        if (!isEditMode) {
          return (
            <Box>
              <Typography variant='fieldLabel' sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {children}
                {label}
              </Typography>
              <Typography variant='fieldValue'>
                {field.value ? (formatDisplayValue ? formatDisplayValue(field.value) : field.value) : '-'}
              </Typography>
            </Box>
          )
        }

        if (type === 'date') {
          const dateValue = field.value ? parse(field.value, 'yyyy-MM-dd', new Date()) : null
          return (
            <Box>
              <Typography variant='fieldLabel' sx={{ mb: 1 }}>
                {label}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                <DatePicker
                  value={isValid(dateValue) ? dateValue : null}
                  onChange={(date: Date | null) => {
                    field.onChange(date && isValid(date) ? format(date, 'yyyy-MM-dd') : '')
                  }}
                  format='dd/MM/yyyy'
                  disabled={disabled}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      placeholder: 'DD/MM/YYYY',
                      error: !!error,
                      helperText: error
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
          )
        }

        if (type === 'select' && options) {
          return (
            <Box>
              <Typography variant='fieldLabel' sx={{ mb: 1 }}>
                {label}
              </Typography>
              <Select {...field} fullWidth size='small' error={!!error} disabled={disabled}>
                {options.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
              {error && (
                <Typography sx={{ color: colors.status.errorAlt, fontSize: '12px', mt: 0.5 }}>{error}</Typography>
              )}
            </Box>
          )
        }

        // Unlike `select`, this accepts values outside `options` — the suggestions are a
        // shortcut, not a constraint, so pre-populated free text stays editable as typed.
        if (type === 'autocomplete' && options) {
          return (
            <Box>
              <Typography variant='fieldLabel' sx={{ mb: 1 }}>
                {label}
              </Typography>
              <Autocomplete
                freeSolo
                options={options}
                disabled={disabled}
                value={field.value ?? ''}
                // Both handlers write the same field: `onChange` covers picking a
                // suggestion, `onInputChange` covers typing a value of their own.
                onChange={(_, newValue) => field.onChange(newValue ?? '')}
                onInputChange={(_, newInputValue) => field.onChange(newInputValue)}
                renderInput={params => (
                  <TextField
                    {...params}
                    size='small'
                    fullWidth
                    error={!!error}
                    helperText={error}
                    slotProps={{ htmlInput: { ...params.inputProps, maxLength } }}
                  />
                )}
              />
            </Box>
          )
        }

        return (
          <Box>
            <Typography variant='fieldLabel' sx={{ mb: 1 }}>
              {label}
            </Typography>
            <TextField {...field} fullWidth size='small' error={!!error} helperText={error} disabled={disabled} />
          </Box>
        )
      }}
    />
  )
}
